import { Dispatch } from 'redux'
import { api } from '../../Controllers/api'
import { Project, ProjectInput, ArchiveType, ProcoreProject } from 'Projects/Types/types'
import { store } from 'index'

export const PROJECTS_LOADING = 'PROJECTS_LOADING'
export const ERROR = 'ERROR'
export const RECEIVE_PROJECTS = 'RECEIVE_PROJECTS'
export const EDIT_PROJECT = 'EDIT_PROJECT'
export const CREATE_PROJECT = 'CREATE_PROJECT'
export const PROCORE_LOADING = 'PROCORE_LOADING'
export const IMPORT_PROCORE_PROJECTS = 'IMPORT_PROCORE_PROJECTS'
export const SAVE_PROCORE_PROJECTS = 'SAVE_PROCORE_PROJECTS'

export interface ProjectsLoadingAction {
  type: typeof PROJECTS_LOADING
  loading: boolean
}

export interface ErrorAction {
  type: typeof ERROR
  loading: boolean
  procoreLoading: boolean
  error: string
}

export interface GetProjectsAction {
  type: typeof RECEIVE_PROJECTS
  payload: { data: Project[] }
  loading: boolean
}

export interface AddProjectAction {
  type: typeof CREATE_PROJECT
  payload: { data: Project }
  loading: boolean
}

export interface UpdateProjectAction {
  type: typeof EDIT_PROJECT
  payload: { data: Project }
  loading: boolean
}

export interface ProcoreLoadingAction {
  type: typeof PROCORE_LOADING
  procoreLoading: boolean
}

export interface ImportProcoreProjectsAction {
  type: typeof IMPORT_PROCORE_PROJECTS
  payload: { data: ProcoreProject[] }
  procoreLoading: boolean
}

export interface SaveProcoreProjectsAction {
  type: typeof SAVE_PROCORE_PROJECTS
  payload: { data: ProcoreProject[] }
  procoreLoading: boolean
}

const projectsLoading = (loading: boolean): ProjectsLoadingAction => {
  return {
    type: PROJECTS_LOADING,
    loading
  }
}

const error = (error: string): ErrorAction => {
  return {
    type: ERROR,
    loading: false,
    procoreLoading: false,
    error
  }
}

const receiveProjects = (data: Project[]): GetProjectsAction => {
  return {
    type: RECEIVE_PROJECTS,
    payload: { data },
    loading: false
  }
}

const createdProject = (data: Project): AddProjectAction => {
  return {
    type: CREATE_PROJECT,
    payload: { data },
    loading: false
  }
}

const editedProject = (data: Project): UpdateProjectAction => {
  return {
    type: EDIT_PROJECT,
    payload: { data },
    loading: false
  }
}

const procoreLoading = (procoreLoading: boolean): ProcoreLoadingAction => {
  return {
    type: PROCORE_LOADING,
    procoreLoading
  }
}

const importProjects = (data: ProcoreProject[]): ImportProcoreProjectsAction => {
  return {
    type: IMPORT_PROCORE_PROJECTS,
    payload: { data },
    procoreLoading: false
  }
}

const saveProjects = (data: ProcoreProject[]): SaveProcoreProjectsAction => {
  return {
    type: SAVE_PROCORE_PROJECTS,
    payload: { data },
    procoreLoading: false
  }
}

const getProjects = (isRefresh: boolean) => {
  return (dispatch: Dispatch<ProjectsLoadingAction | GetProjectsAction>) => {
    if (!isRefresh) {
      dispatch(projectsLoading(true))
    }
    api
      .get(`projects`)
      .then(({ data }) => {
        dispatch(receiveProjects(data))
      })
      .catch(err => console.error(err))
  }
}

const createProject = (project: Project) => {
  return (dispatch: Dispatch<AddProjectAction | ProjectsLoadingAction>) => {
    dispatch(projectsLoading(true))
    api
      .post(`projects`, project)
      .then(({ data }) => {
        dispatch(createdProject({ ...project, _id: data.insertedId, active: true, delete: false }))
      })
      .catch(err => console.error(err))
  }
}

const editProject = (_id: string, project: ProjectInput) => {
  return (dispatch: Dispatch<UpdateProjectAction | ProjectsLoadingAction>) => {
    dispatch(projectsLoading(true))
    api
      .put(`projects/${_id}`, project)
      .then(({ data }) => {
        dispatch(editedProject({ ...project, _id, active: true, delete: false }))
      })
      .catch(err => console.error(err))
  }
}

const archiveUnarchiveProject = (_id: string, body: Project, archiveUnarchive: ArchiveType) => {
  return (dispatch: Dispatch<UpdateProjectAction | ProjectsLoadingAction>) => {
    dispatch(projectsLoading(true))
    api
      .put(`projects/${archiveUnarchive}/${_id}`, {})
      .then(({ data }) => {
        dispatch(
          editedProject({
            ...body,
            _id,
            active: archiveUnarchive === 'archive' ? false : true,
            delete: false
          })
        )
      })
      .catch(err => console.error(err))
  }
}

const importProcoreProjects = (token: string) => {
  return (dispatch: Dispatch<ImportProcoreProjectsAction | ProcoreLoadingAction | ErrorAction>) => {
    dispatch(procoreLoading(true))
    api
      .post('procore/get-projects', { token })
      .then(({ data }) => {
        dispatch(importProjects(data))
      })
      .catch(err => {
        console.error(err)
        dispatch(error('Error importing procore projects'))
      })
  }
}

const saveProcoreProjects = (procoreProjects: ProcoreProject[], token: string) => {
  return (dispatch: Dispatch<SaveProcoreProjectsAction | ProcoreLoadingAction | ErrorAction>) => {
    dispatch(procoreLoading(true))
    api
      .post('procore/projects', { token, procoreProjects })
      .then(({ data }) => {
        dispatch(saveProjects(data))
      })
      .catch(err => {
        console.error(err)
        dispatch(error('Error saving procore projecrts'))
      })
  }
}

const syncProcoreProjects = (projects: Project[], token: string) => {
  return (dispatch: Dispatch<ProcoreLoadingAction | ErrorAction>) => {
    dispatch(procoreLoading(true))
    api
      .post('procore/sync-projects', { token, projects })
      .then(() => {
        dispatch(procoreLoading(false))
        store.dispatch(getProjects(true))
      })
      .catch(err => {
        console.error(err)
        dispatch(error('Error syncing procore projects'))
      })
  }
}

export {
  getProjects,
  createProject,
  editProject,
  archiveUnarchiveProject,
  saveProcoreProjects,
  importProcoreProjects,
  syncProcoreProjects
}
